// UI only for now (user decision 2026-09-24): Announcement settings has no api calls; these are the spec's placeholder values.
// Exception: `domain` is loaded and saved through the v1 email marketing settings (2026-10-15).

export interface RuleSet {
    email: boolean;
    confirm: boolean;
}

export interface AnnouncementForm {
    priceSensitive: RuleSet;
    other: RuleSet;
    // the stock's AutoSendFilter regex, matched against the headline ignoring case
    filter: string;
    // the stock's ConfirmationEmail
    approvers: string[];
    asx: { template: string; subject: string };
    welcome: { template: string; subject: string; send: boolean; when: string };
    // extra addresses that get test sends, besides the signed-in user
    testEmails: string[];
    social: { priceSensitive: boolean; afterApproval: boolean; askRoutine: boolean; picture: boolean; wording: string };
    domain: { from: string; fromName: string };
}

export const initialForm = (ticker: string, short: string): AnnouncementForm => ({
    priceSensitive: { email: false, confirm: false },
    other: { email: false, confirm: false },
    filter: '',
    approvers: [],
    asx: {
        template: 'asx-system',
        subject: `${ticker} New Announcement – {{announcement_headline}}`,
    },
    welcome: { template: 'welcome-system', subject: `Welcome to ${short} investor updates`, send: true, when: 'now' },
    testEmails: [],
    social: {
        priceSensitive: true,
        afterApproval: true,
        askRoutine: true,
        picture: false,
        wording: `{{announcement_headline}}\n\nRead the full announcement: {{announcement_link}}\n\n#ASX #${ticker} #gold`,
    },
    domain: { from: '', fromName: '' },
});

// The auto-send filter stays a regex (v1 AutoSendFilter) so existing patterns keep working.
// Most clients only need a list of words, so the page shows chips whenever the regex is just `word|word|word`
// and saves chips back as that same regex; anything more complex is edited as a raw pattern instead.
const META = /[\\^$.|?*+()[\]{}]/g;
const PLAIN_PART = /^(?:[^\\^$.|?*+()[\]{}]|\\[\\^$.|?*+()[\]{}])+$/;

export const getFilterWords = (filter: string): string[] | null => {
    if (!filter.trim()) return [];
    const parts = filter.split('|').map(x => x.trim());
    if (!parts.every(x => PLAIN_PART.test(x))) return null;
    return parts.map(x => x.replace(/\\(.)/g, '$1'));
};

export const getFilterPattern = (words: string[]) => words.map(w => w.replace(META, '\\$&')).join('|');

export const STANDARD_FILTERS = [
    { word: 'Appendix 3Y', hint: 'Change of director’s interest' },
    { word: 'Appendix 3B', hint: 'New issue announcement' },
    { word: 'Appendix 2A', hint: 'Application for quotation of securities' },
    { word: 'Cleansing notice', hint: 'Section 708A notice' },
    { word: 'Notice of meeting', hint: 'AGM and EGM notices' },
    { word: 'Change of address', hint: 'Registered office changes' },
    { word: 'Becoming a substantial holder', hint: 'Form 603 / 604 / 605' },
    { word: 'Trading halt', hint: 'Some companies do want these emailed' },
];

export const ASX_TEMPLATES = [
    { value: 'asx-system', label: 'ASX Announcement (system template)' },
    { value: 'asx-brand', label: 'ASX Announcement (brand template)' },
];
export const WELCOME_TEMPLATES = [
    { value: 'welcome-system', label: 'Welcome Email (system template)' },
    { value: 'welcome-brand', label: 'Welcome Email (brand template)' },
];
export const SEND_WHEN = [
    { value: 'now', label: 'Straight away after someone subscribes' },
    { value: 'hour', label: '1 hour after someone subscribes' },
    { value: 'morning', label: 'The next morning at 9:00 am after someone subscribes' },
];
export const TEST_ANNOUNCEMENTS = [
    { value: '3y', label: '1‑Sep‑26 · Appendix 3Y – Change of Director’s Interest' },
    { value: 'drill', label: '28‑Aug‑26 · 171m @ 0.9 g/t Gold Confirms Scale at Gold Mountain' },
    { value: 'quarterly', label: '31‑Jul‑26 · Quarterly Activities Report' },
];

export interface SocialAccount {
    id: string;
    name: string;
    detail: string;
    connected: boolean;
}

export const SOCIAL_ACCOUNTS: SocialAccount[] = [
    { id: 'linkedin', name: 'LinkedIn', detail: 'Not connected', connected: false },
    { id: 'x', name: 'X', detail: 'Not connected', connected: false },
];

export const SAMPLE_POST = {
    title: '171m @ 0.9 g/t Gold Confirms Scale at Gold Mountain',
};
